// src/components/album/FloatingReactions.jsx
import React, { useState, useEffect } from 'react';
import { Heart, MessageCircle, Camera, Mic } from 'lucide-react';

const FloatingReaction = ({ reaction, onComplete }) => {
  const [position, setPosition] = useState({
    bottom: 100,
    left: Math.random() * 80 + 10, // Random position between 10% and 90%
    opacity: 1
  });

  useEffect(() => {
    // Animate upward
    const animationFrame = requestAnimationFrame(() => {
      setPosition(prev => ({
        ...prev,
        bottom: prev.bottom + 200 + Math.random() * 100, // Float up 200-300px
        opacity: 0
      }));
    });

    // Remove after animation
    const timeout = setTimeout(() => {
      onComplete(reaction.id);
    }, 2500);

    return () => {
      cancelAnimationFrame(animationFrame);
      clearTimeout(timeout);
    };
  }, [reaction.id, onComplete]);

  const getReactionIcon = () => {
    switch (reaction.type) {
      case 'like':
        return <Heart className="h-6 w-6 text-red-500 fill-current" />;
      case 'comment':
        return <MessageCircle className="h-6 w-6 text-blue-500" />;
      case 'audio':
        return <Mic className="h-6 w-6 text-green-500" />;
      case 'photo':
        return <Camera className="h-6 w-6 text-purple-500" />;
      default:
        return <span className="text-2xl">❤️</span>;
    }
  };

  return (
    <div
      className="absolute pointer-events-none transition-all duration-2500 ease-out z-30"
      style={{
        bottom: `${position.bottom}px`,
        left: `${position.left}%`,
        opacity: position.opacity,
        transform: `translateX(-50%) scale(${position.opacity})`,
      }}
    >
      <div className="flex items-center gap-2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full backdrop-blur-sm">
        {getReactionIcon()}
        <span className="text-sm font-medium">
          {reaction.userName || 'Someone'}
        </span>
      </div>
    </div>
  );
};

const FloatingReactions = ({ liveReactions = [] }) => {
  const [activeReactions, setActiveReactions] = useState([]);

  useEffect(() => {
    // Add new reactions to active list
    liveReactions.forEach(reaction => {
      setActiveReactions(prev => {
        // Avoid duplicates
        if (prev.some(r => r.id === reaction.id)) return prev;
        return [...prev, { ...reaction, animationId: Date.now() + Math.random() }];
      });
    });
  }, [liveReactions]);

  const handleReactionComplete = (reactionId) => {
    setActiveReactions(prev => prev.filter(r => r.id !== reactionId));
  };

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {activeReactions.map(reaction => (
        <FloatingReaction
          key={`${reaction.id}-${reaction.animationId}`}
          reaction={reaction}
          onComplete={handleReactionComplete}
        />
      ))}
    </div>
  );
};

export default FloatingReactions;